/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Icon, Badge, Button } from 'sarsaparilla';
import { useAuthModal } from 'shared-ui/auth';
import { useDebounce } from 'shared-ui/utils/useDebounce';
import { UIcon } from 'shared-ui/uicon';
import { SafariToolbarColorFixer } from 'shared-ui/utils/SafariToolbarColorFixer';

import * as searchActions from '../../actions/search';
import { useSearchResultsTotalQuery } from '../../api/search.queries';
import { TripPreferenceProvider } from '../../components/tripPreferences/TripPreferenceContext';
import { useAppDispatch, useAppSelector } from '../../redux/redux-helpers';
import { ResponsiveDialog } from '../ResponsiveDialog';

import {
    getPendingFilters,
    hasPendingChanges,
    pendingFiltersReducer,
} from './pendingFilters';
import styles from './SearchFiltersDialog.module.scss';
import {
    getActiveFiltersForGa,
    getAppliedResultsCount,
    getResultsButtonLabel,
    getSearchFiltersCount,
    type ResultsCount,
} from './SearchFiltersDialog.utils';
import { SearchFiltersSections } from './SearchFiltersSections';
import { TripPreferences } from './TripPreferences';

/** How long the filters must stay unchanged before the results count is fetched */
const RESULTS_COUNT_DELAY = 300;

// Same id as the desktop "More Filters" button, only one of them renders at a time
const TRIGGER_ID = 'search-header-filters-btn';

const getTriggerElement = () => document.getElementById(TRIGGER_ID);

interface SearchFiltersDialogTriggerProps {
    filtersCount: number;
    isOpen: boolean;
    onClick: () => void;
}

/**
 * Opens the filters dialog. Sits in the search header, apart from the dialog,
 * the same way the desktop "More Filters" button sits apart from its panel.
 */
export function SearchFiltersDialogTrigger({
    filtersCount,
    isOpen,
    onClick,
}: SearchFiltersDialogTriggerProps) {
    return (
        <button
            type="button"
            id={TRIGGER_ID}
            className={styles.MenuButton}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            onClick={onClick}
            data-ga-tracking-id="554715340412"
        >
            <Icon iconName="tune" />
            Filters
            {filtersCount > 0 && (
                <Badge appearance="primary" className="ml-half">
                    {filtersCount}
                </Badge>
            )}
        </button>
    );
}

interface SearchFiltersDialogProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    /**
     * Goes back to the first page and searches. Called once the filters from
     * the dialog are in the store.
     */
    onApplyFilters: () => void;
}

/**
 * Search filters in a bottom drawer on small viewports (`isSmAndDown`) and a
 * centered dialog on larger ones. Filter changes stay in the dialog, which
 * shows how many results they would find, until the results button applies
 * them. Closing the dialog any other way discards them.
 */
export function SearchFiltersDialog({
    isOpen,
    onOpenChange,
    onApplyFilters,
}: SearchFiltersDialogProps) {
    const dispatch = useAppDispatch();
    const search = useAppSelector((state) => state.search);
    const searchResults = useAppSelector((state) => state.searchResults);
    const [isLoggingIn, setIsLoggingIn] = React.useState(false);
    const { ensureLoggedIn } = useAuthModal();

    const applied = React.useMemo(() => getPendingFilters(search), [search]);
    const [pending, dispatchPending] = React.useReducer(pendingFiltersReducer, applied);
    const debouncedPending = useDebounce(pending, RESULTS_COUNT_DELAY);

    // Each time the dialog opens, start over from the applied filters
    const [wasOpen, setWasOpen] = React.useState(isOpen);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) {
            dispatchPending({ type: 'reset', pending: applied });
        }
    }

    const isDebouncing = debouncedPending !== pending;
    const isChanged = hasPendingChanges(pending, applied);

    const pendingSearch = React.useMemo(
        () => ({ ...search, ...pending }),
        [search, pending]
    );
    const debouncedSearch = React.useMemo(
        () => ({ ...search, ...debouncedPending }),
        [search, debouncedPending]
    );

    const resultsTotalQuery = useSearchResultsTotalQuery(debouncedSearch, {
        enabled: isOpen && isChanged && !isDebouncing,
    });

    let resultsCount: ResultsCount;
    if (!isChanged) {
        resultsCount = getAppliedResultsCount(searchResults);
    } else if (isDebouncing || resultsTotalQuery.isPending) {
        resultsCount = { status: 'loading' };
    } else if (resultsTotalQuery.isError) {
        resultsCount = { status: 'error' };
    } else {
        resultsCount = { status: 'success', total: resultsTotalQuery.data ?? 0 };
    }

    const resultsLabel = getResultsButtonLabel(resultsCount);
    const isResultsCountLoading = resultsCount.status === 'loading';

    // Keep the last count's label while loading, since the button fades its label out
    // under the spinner instead of hiding it right away
    const [settledResultsLabel, setSettledResultsLabel] = React.useState(resultsLabel);
    if (!isResultsCountLoading && resultsLabel !== settledResultsLabel) {
        setSettledResultsLabel(resultsLabel);
    }

    // Hide the dialog while the login modal is up, since it would open behind it
    const onLoginClick = async () => {
        setIsLoggingIn(true);

        try {
            await ensureLoggedIn();
        } catch {
            // Nothing to do, the dialog reopens either way
        }

        setIsLoggingIn(false);
    };

    const onShowResults = () => {
        onOpenChange(false);

        if (!isChanged) {
            return;
        }

        dispatch(searchActions.updateSearchCriterias(pending));
        onApplyFilters();
    };

    const onClearAll = () => {
        dispatchPending({ type: 'clearAll' });
    };

    const filtersCount = getSearchFiltersCount(pendingSearch);

    return (
        <ResponsiveDialog.Root open={isOpen && !isLoggingIn} onOpenChange={onOpenChange}>
            <ResponsiveDialog.Portal className={styles.Portal}>
                <ResponsiveDialog.Backdrop className={styles.Backdrop} />
                <ResponsiveDialog.Viewport className={styles.Viewport}>
                    <ResponsiveDialog.Popup
                        className={styles.Popup}
                        // Focus stays in the login modal, the dialog reopens after it
                        finalFocus={() => (isLoggingIn ? false : getTriggerElement())}
                    >
                        <ResponsiveDialog.Handle className={styles.Handle} />
                        <ResponsiveDialog.Content className={styles.Content}>
                            <div className={styles.Header}>
                                <ResponsiveDialog.Title className={styles.Title}>
                                    Filters
                                </ResponsiveDialog.Title>
                                <ResponsiveDialog.Close
                                    aria-label="Close Filters"
                                    className={styles.Close}
                                    id="hook-hide"
                                >
                                    <UIcon name="x" size={20} />
                                </ResponsiveDialog.Close>
                            </div>

                            <div className={styles.Body}>
                                <div className={styles.BodyViewport}>
                                    <TripPreferenceProvider>
                                        <TripPreferences
                                            onLoginClick={onLoginClick}
                                            isEnabled={pending.tripPreferences}
                                            onChange={dispatchPending}
                                        />
                                    </TripPreferenceProvider>

                                    <SearchFiltersSections
                                        pending={pending}
                                        hasDates={Boolean(
                                            search?.checkin_time && search?.checkout_time
                                        )}
                                        onChange={dispatchPending}
                                    />
                                </div>
                            </div>

                            <div className={styles.Actions}>
                                <Button
                                    appearance="subtle"
                                    className={styles.clearAllButton}
                                    id="hook-reset"
                                    onClick={onClearAll}
                                    isDisabled={filtersCount === 0}
                                >
                                    Clear Filters
                                </Button>
                                <Button
                                    appearance="primary"
                                    id="hook-apply"
                                    onClick={onShowResults}
                                    isLoading={isResultsCountLoading}
                                    className={styles.resultsButton}
                                    clickTagCategory="Search Results Filters"
                                    clickTagAction={`Filter By - ${
                                        filtersCount <= 0
                                            ? 'No Filters Selected'
                                            : getActiveFiltersForGa(pendingSearch)
                                    }`}
                                    clickTagLabel="View Results Button"
                                >
                                    {isResultsCountLoading
                                        ? settledResultsLabel
                                        : resultsLabel}
                                </Button>
                            </div>
                        </ResponsiveDialog.Content>
                    </ResponsiveDialog.Popup>
                </ResponsiveDialog.Viewport>
            </ResponsiveDialog.Portal>
            {/* Correctly tint Safari's toolbar */}
            {isOpen && <SafariToolbarColorFixer cssColorVar="--color-ui-03" />}
        </ResponsiveDialog.Root>
    );
}

// cSpell:ignore checkin
